import React, { useEffect, useState } from 'react'
import {
  Box,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  LinearProgress,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Chip
} from '@mui/material'
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material'
import { useAuth } from '../../contexts/AuthContext'
import { billingApi, institutionService, userService } from '../../services/api'
import {
  Institution,
  UserWithProfile,
  BillingReportSegmentInstitutionResponse,
  BillingReportSegmentDoctorResponse
} from '../../types/api'

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(value || 0)

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('ro-RO', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date)
}

interface SegmentReportsTabProps {
  activeSubTab: 'institutions' | 'doctors'
}

const SegmentReportsTab: React.FC<SegmentReportsTabProps> = ({ activeSubTab }) => {
  const { user } = useAuth()

  // Institutions and doctors list
  const [institutions, setInstitutions] = useState<Institution[]>([])
  const [doctors, setDoctors] = useState<UserWithProfile[]>([])
  const [selectedInstitution, setSelectedInstitution] = useState('')
  const [selectedDoctor, setSelectedDoctor] = useState('')

  // Period selection
  const [periodType, setPeriodType] = useState<'monthly' | 'custom'>('monthly')
  const [month, setMonth] = useState(new Date().getMonth() + 1)
  const [year, setYear] = useState(new Date().getFullYear())
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')

  // Filters
  const [statusFilter, setStatusFilter] = useState('')

  // Data
  const [institutionReport, setInstitutionReport] = useState<BillingReportSegmentInstitutionResponse | null>(null)
  const [doctorReport, setDoctorReport] = useState<BillingReportSegmentDoctorResponse | null>(null)

  // UI State
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load institutions and doctors on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [institutionsRes, doctorsRes] = await Promise.all([
          institutionService.getInstitutions({ limit: 200 }),
          userService.getUsers({ role: 'medic', limit: 200 })
        ])
        setInstitutions(institutionsRes.institutions)
        setDoctors(doctorsRes.users)
      } catch (err) {
        console.error('Error loading data', err)
        setError('Eroare la încărcarea datelor')
      }
    }

    loadData()
  }, [])

  // Load report when filters change
  useEffect(() => {
    if (selectedInstitution && activeSubTab === 'institutions') {
      loadInstitutionReport()
    } else if (selectedDoctor && selectedInstitution && activeSubTab === 'doctors') {
      loadDoctorReport()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInstitution, selectedDoctor, periodType, month, year, fromDate, toDate, statusFilter, activeSubTab])

  const loadInstitutionReport = async () => {
    if (!selectedInstitution) return

    try {
      setError(null)
      setLoading(true)

      const params: any = {
        institution_id: selectedInstitution,
        status_filter: statusFilter || undefined
      }

      if (periodType === 'monthly') {
        params.month = month
        params.year = year
      } else {
        params.from_date = fromDate
        params.to_date = toDate
      }

      const response = await billingApi.getInstitutionSegmentReport(params)
      setInstitutionReport(response)
    } catch (err: any) {
      console.error('Error loading institution segment report', err)
      setError(err?.response?.data?.detail || 'Eroare la încărcarea raportului instituție')
    } finally {
      setLoading(false)
    }
  }

  const loadDoctorReport = async () => {
    if (!selectedDoctor || !selectedInstitution) return

    try {
      setError(null)
      setLoading(true)

      const params: any = {
        doctor_id: selectedDoctor,
        institution_id: selectedInstitution,
        status_filter: statusFilter || undefined
      }

      if (periodType === 'monthly') {
        params.month = month
        params.year = year
      } else {
        params.from_date = fromDate
        params.to_date = toDate
      }

      const response = await billingApi.getDoctorSegmentReport(params)
      setDoctorReport(response)
    } catch (err: any) {
      console.error('Error loading doctor segment report', err)
      setError(err?.response?.data?.detail || 'Eroare la încărcarea raportului medic')
    } finally {
      setLoading(false)
    }
  }

  const handleExport = async (format: 'pdf' | 'excel' | 'csv') => {
    // Validare diferită în funcție de tab
    if (activeSubTab === 'institutions' && !selectedInstitution) return
    if (activeSubTab === 'doctors' && (!selectedDoctor || !selectedInstitution)) return

    try {
      setLoading(true)

      const params: any = {
        format,
        status_filter: statusFilter || undefined
      }

      // Adaugă parametrii specifici tab-ului
      if (activeSubTab === 'institutions') {
        params.institution_id = selectedInstitution
      } else {
        params.doctor_id = selectedDoctor
        params.institution_id = selectedInstitution
      }

      if (periodType === 'monthly') {
        params.month = month
        params.year = year
      } else {
        params.from_date = fromDate
        params.to_date = toDate
      }

      // Apelează endpoint-ul corect în funcție de tab
      const blob = activeSubTab === 'institutions'
        ? await billingApi.exportSegmentReport(params)
        : await billingApi.exportDoctorSegmentReport(params)

      // Download file
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      let extension = 'csv'
      if (format === 'pdf') extension = 'pdf'
      else if (format === 'excel') extension = 'xlsx'
      
      const filePrefix = activeSubTab === 'institutions' 
        ? `raport_affidea_${selectedInstitution}` 
        : `raport_medic_${selectedDoctor}_${selectedInstitution}`
      const filename = `${filePrefix}_${new Date().toISOString().split('T')[0]}.${extension}`
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err: any) {
      console.error('Error exporting report', err)
      setError(err?.response?.data?.detail || 'Eroare la exportul raportului')
    } finally {
      setLoading(false)
    }
  }

  if (!user || !['admin', 'operator'].includes(user.role)) {
    return (
      <Alert severity="error">Nu aveți permisiuni pentru a accesa rapoartele de billing.</Alert>
    )
  }

  const inputSx = { 
    '& .MuiInputBase-root': { fontSize: '0.75rem', height: 28 }, 
    '& .MuiInputLabel-root': { fontSize: '0.7rem' },
    '& .MuiMenuItem-root': { fontSize: '0.75rem' }
  }

  return (
    <Box>
      {/* Filters - compact inline */}
      <Paper sx={{ p: 1.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
          {/* Institution Selection */}
          <FormControl size="small" sx={{ minWidth: 160, ...inputSx }}>
            <InputLabel>Instituție</InputLabel>
            <Select
              value={selectedInstitution}
              onChange={(e) => setSelectedInstitution(e.target.value)}
              label="Instituție"
            >
              <MenuItem value="" sx={{ fontSize: '0.75rem' }}>
                <em>Selectează</em>
              </MenuItem>
              {institutions.map((inst) => (
                <MenuItem key={inst.id} value={inst.id} sx={{ fontSize: '0.75rem' }}>
                  {inst.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Doctor Selection - doar pentru tab doctors */}
          {activeSubTab === 'doctors' && (
            <FormControl size="small" sx={{ minWidth: 160, ...inputSx }}>
              <InputLabel>Medic</InputLabel>
              <Select
                value={selectedDoctor}
                onChange={(e) => setSelectedDoctor(e.target.value)}
                label="Medic"
              >
                <MenuItem value="" sx={{ fontSize: '0.75rem' }}>
                  <em>Selectează</em>
                </MenuItem>
                {doctors.map((doc) => (
                  <MenuItem key={doc.id} value={doc.id} sx={{ fontSize: '0.75rem' }}>
                    {doc.doctor_profile?.first_name} {doc.doctor_profile?.last_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Period Type Toggle */}
          <ToggleButtonGroup
            value={periodType}
            exclusive
            onChange={(_, newValue) => newValue && setPeriodType(newValue)}
            size="small"
            sx={{ '& .MuiToggleButton-root': { fontSize: '0.72rem', py: 0.25, px: 1.5, height: 28, textTransform: 'none' } }}
          >
            <ToggleButton value="monthly">Lunar</ToggleButton>
            <ToggleButton value="custom">Interval Custom</ToggleButton>
          </ToggleButtonGroup>

          {/* Status Filter */}
          <FormControl size="small" sx={{ minWidth: 100, ...inputSx }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="" sx={{ fontSize: '0.75rem' }}>Toate</MenuItem>
              <MenuItem value="finalized" sx={{ fontSize: '0.75rem' }}>Finalizate</MenuItem>
              <MenuItem value="assigned" sx={{ fontSize: '0.75rem' }}>Alocate</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Second row: Period selection + buttons */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Period Selection - Monthly */}
          {periodType === 'monthly' && (
            <>
              <FormControl size="small" sx={{ minWidth: 130, ...inputSx }}>
                <InputLabel>Luna</InputLabel>
                <Select
                  value={month}
                  onChange={(e) => setMonth(Number(e.target.value))}
                  label="Luna"
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <MenuItem key={m} value={m} sx={{ fontSize: '0.75rem' }}>
                      {new Date(2000, m - 1).toLocaleString('ro-RO', { month: 'long' })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Anul"
                type="number"
                size="small"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                InputProps={{ inputProps: { min: 2020, max: 2100 } }}
                sx={{ width: 70, ...inputSx }}
              />
            </>
          )}

          {/* Period Selection - Custom */}
          {periodType === 'custom' && (
            <>
              <TextField
                label="De la"
                type="date"
                size="small"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 130, ...inputSx }}
              />
              <TextField
                label="Până la"
                type="date"
                size="small"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 130, ...inputSx }}
              />
            </>
          )}

          {/* Action Buttons - compact */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
            onClick={activeSubTab === 'institutions' ? loadInstitutionReport : loadDoctorReport}
            disabled={
              activeSubTab === 'institutions' 
                ? !selectedInstitution || loading
                : !selectedDoctor || !selectedInstitution || loading
            }
            sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none', px: 1.5 }}
          >
            Actualizează
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
            onClick={() => handleExport('csv')}
            disabled={
              activeSubTab === 'institutions'
                ? !institutionReport || loading
                : !doctorReport || loading
            }
            sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none', px: 1.5 }}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PdfIcon sx={{ fontSize: 14 }} />}
            onClick={() => handleExport('pdf')}
            disabled={
              activeSubTab === 'institutions'
                ? !institutionReport || loading
                : !doctorReport || loading
            }
            sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none', px: 1.5 }}
          >
            Export PDF
          </Button>
        </Box>
      </Paper>

      {/* Loading */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Institution Report */}
      {activeSubTab === 'institutions' && institutionReport && (
        <Paper sx={{ p: 3 }}>
          <Box mb={3}>
            <Typography variant="h5" gutterBottom>
              Raport Segmente - {institutionReport.institution_name}
            </Typography>
            <Box display="flex" gap={2} mt={2}>
              <Chip label={`Total Segmente: ${institutionReport.total_segments}`} color="primary" />
              <Chip label={`Total: ${formatCurrency(institutionReport.total_amount)}`} color="success" />
              {institutionReport.missing_prices_count > 0 && (
                <Chip
                  label={`Prețuri lipsă: ${institutionReport.missing_prices_count}`}
                  color="warning"
                />
              )}
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Locație</TableCell>
                  <TableCell>Pacient</TableCell>
                  <TableCell>Investigație</TableCell>
                  <TableCell>Segment</TableCell>
                  <TableCell align="right">Nr Seg</TableCell>
                  <TableCell align="right">Preț/Seg</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {institutionReport.summaries.map((summary, summaryIdx) => (
                  <React.Fragment key={`summary-${summaryIdx}`}>
                    {/* Header row pentru medic */}
                    <TableRow sx={{ backgroundColor: 'action.hover' }}>
                      <TableCell colSpan={8}>
                        <Typography variant="h6">
                          {summary.doctor_name}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Iterăm prin grupurile de locații */}
                    {summary.location_groups.map((locationGroup, locIdx) => (
                      <React.Fragment key={`location-${summaryIdx}-${locIdx}`}>
                        {/* Header row pentru locație */}
                        <TableRow sx={{ backgroundColor: 'grey.50' }}>
                          <TableCell colSpan={8}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 500, ml: 2 }}>
                              📍 {locationGroup.location_name || 'Fără locație'}
                            </Typography>
                          </TableCell>
                        </TableRow>

                        {/* Entries pentru această locație */}
                        {locationGroup.entries.map((entry, entryIdx) => (
                          <TableRow key={`entry-${summaryIdx}-${locIdx}-${entryIdx}`}>
                            <TableCell sx={{ pl: 4 }}>{formatDate(entry.study_date)}</TableCell>
                            <TableCell>{entry.location_name || 'N/A'}</TableCell>
                            <TableCell>{entry.patient_name}</TableCell>
                            <TableCell>{entry.investigation_name || 'N/A'}</TableCell>
                            <TableCell>{entry.segment_name}</TableCell>
                            <TableCell align="right">{entry.segment_count}</TableCell>
                            <TableCell align="right">{formatCurrency(entry.price_per_segment)}</TableCell>
                            <TableCell align="right">{formatCurrency(entry.total_price)}</TableCell>
                          </TableRow>
                        ))}

                        {/* Subtotal locație */}
                        <TableRow sx={{ backgroundColor: 'grey.100' }}>
                          <TableCell colSpan={5} align="right" sx={{ fontStyle: 'italic' }}>
                            Subtotal {locationGroup.location_name || 'Fără locație'}:
                          </TableCell>
                          <TableCell align="right" sx={{ fontStyle: 'italic' }}>
                            {locationGroup.total_segments}
                          </TableCell>
                          <TableCell align="right"></TableCell>
                          <TableCell align="right" sx={{ fontStyle: 'italic' }}>
                            {formatCurrency(locationGroup.total_amount)}
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}

                    {/* Subtotal medic */}
                    <TableRow sx={{ backgroundColor: 'grey.200' }}>
                      <TableCell colSpan={5} align="right">
                        <strong>Subtotal {summary.doctor_name}:</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{summary.total_segments}</strong>
                      </TableCell>
                      <TableCell align="right"></TableCell>
                      <TableCell align="right">
                        <strong>{formatCurrency(summary.total_amount)}</strong>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}

                {/* Grand total */}
                <TableRow sx={{ backgroundColor: 'primary.light' }}>
                  <TableCell colSpan={5} align="right">
                    <strong>TOTAL GENERAL:</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{institutionReport.total_segments}</strong>
                  </TableCell>
                  <TableCell align="right"></TableCell>
                  <TableCell align="right">
                    <strong>{formatCurrency(institutionReport.total_amount)}</strong>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Doctor Report */}
      {activeSubTab === 'doctors' && doctorReport && (
        <Paper sx={{ p: 3 }}>
          <Box mb={3}>
            <Typography variant="h5" gutterBottom>
              Raport Segmente - {doctorReport.doctor_name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Instituție: {doctorReport.institution_name}
            </Typography>
            <Box display="flex" gap={2} mt={2}>
              <Chip label={`Total Segmente: ${doctorReport.total_segments}`} color="primary" />
              <Chip label={`Total: ${formatCurrency(doctorReport.total_amount)}`} color="success" />
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Locație</TableCell>
                  <TableCell>Pacient</TableCell>
                  <TableCell>Investigație</TableCell>
                  <TableCell>Segment</TableCell>
                  <TableCell align="right">Nr Seg</TableCell>
                  <TableCell align="right">Preț/Seg</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {doctorReport.entries.map((entry, idx) => (
                  <TableRow key={`entry-${idx}`}>
                    <TableCell>{formatDate(entry.study_date)}</TableCell>
                    <TableCell>{entry.location_name || 'N/A'}</TableCell>
                    <TableCell>{entry.patient_name}</TableCell>
                    <TableCell>{entry.investigation_name || 'N/A'}</TableCell>
                    <TableCell>{entry.segment_name}</TableCell>
                    <TableCell align="right">{entry.segment_count}</TableCell>
                    <TableCell align="right">{formatCurrency(entry.price_per_segment)}</TableCell>
                    <TableCell align="right">{formatCurrency(entry.total_price)}</TableCell>
                  </TableRow>
                ))}

                {/* Total */}
                <TableRow sx={{ backgroundColor: 'primary.light' }}>
                  <TableCell colSpan={5} align="right">
                    <strong>TOTAL:</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{doctorReport.total_segments}</strong>
                  </TableCell>
                  <TableCell align="right"></TableCell>
                  <TableCell align="right">
                    <strong>{formatCurrency(doctorReport.total_amount)}</strong>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Empty States - with visible contrast */}
      {!loading && activeSubTab === 'institutions' && !institutionReport && selectedInstitution && (
        <Alert severity="info" sx={{ bgcolor: 'info.dark', color: 'info.contrastText', '& .MuiAlert-icon': { color: 'info.contrastText' }, fontSize: '0.8rem', py: 0.5 }}>
          Selectați perioada și apăsați "Actualizează" pentru a genera raportul.
        </Alert>
      )}

      {!loading && activeSubTab === 'doctors' && !doctorReport && selectedDoctor && selectedInstitution && (
        <Alert severity="info" sx={{ bgcolor: 'info.dark', color: 'info.contrastText', '& .MuiAlert-icon': { color: 'info.contrastText' }, fontSize: '0.8rem', py: 0.5 }}>
          Selectați perioada și apăsați "Actualizează" pentru a genera raportul.
        </Alert>
      )}

      {activeSubTab === 'institutions' && !selectedInstitution && (
        <Alert severity="info" sx={{ bgcolor: 'info.dark', color: 'info.contrastText', '& .MuiAlert-icon': { color: 'info.contrastText' }, fontSize: '0.8rem', py: 0.5 }}>
          Selectați o instituție pentru a începe.
        </Alert>
      )}

      {activeSubTab === 'doctors' && (!selectedDoctor || !selectedInstitution) && (
        <Alert severity="info" sx={{ bgcolor: 'info.dark', color: 'info.contrastText', '& .MuiAlert-icon': { color: 'info.contrastText' }, fontSize: '0.8rem', py: 0.5 }}>
          Selectați un medic și o instituție pentru a începe.
        </Alert>
      )}
    </Box>
  )
}

export default SegmentReportsTab
